import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import './NotFoundPage.css';

const BEST_KEY = 'kra_math_rescue_best_v1';
const QUESTION_COUNT = 6;
const OPERATIONS = ['+', '-', '×', '÷'];

export function createRescueQuestion(difficulty, index, random = Math.random) {
  const maximum = { Easy: 10, Medium: 24, Hard: 60 }[difficulty] || 10;
  const pick = (limit) => Math.floor(random() * limit) + 1;
  const operation = OPERATIONS[index % OPERATIONS.length];
  let first = pick(maximum);
  let second = pick(operation === '×' ? Math.min(maximum, 12) : maximum);
  let answer;
  if (operation === '+') answer = first + second;
  if (operation === '-') {
    [first, second] = [Math.max(first, second), Math.min(first, second)];
    answer = first - second;
  }
  if (operation === '×') answer = first * second;
  if (operation === '÷') {
    answer = pick(maximum);
    second = pick(difficulty === 'Easy' ? 5 : 12);
    first = answer * second;
  }
  const options = [answer, answer + 1, answer + 2, answer + 3];
  for (let position = options.length - 1; position > 0; position -= 1) {
    const swap = Math.floor(random() * (position + 1));
    [options[position], options[swap]] = [options[swap], options[position]];
  }
  return { first, second, operation, answer, options };
}

function readBest() {
  try {
    const stored = Number(localStorage.getItem(BEST_KEY));
    return Number.isFinite(stored) ? Math.min(60, Math.max(0, stored)) : 0;
  } catch {
    return 0;
  }
}

export default function NotFoundPage() {
  const [difficulty, setDifficulty] = useState('Easy');
  const [questionIndex, setQuestionIndex] = useState(0);
  const [question, setQuestion] = useState(() => createRescueQuestion('Easy', 0));
  const [selection, setSelection] = useState(null);
  const [score, setScore] = useState(0);
  const [best, setBest] = useState(readBest);
  const [finished, setFinished] = useState(false);
  const questionRef = useRef(null);
  const correct = selection !== null && selection === question.answer;

  useEffect(() => {
    const previousTitle = document.title;
    document.title = '404 | Math Rescue Challenge | K.R.A Initiative';
    return () => { document.title = previousTitle; };
  }, []);

  const restart = (level = difficulty) => {
    setDifficulty(level);
    setScore(0);
    setQuestionIndex(0);
    setSelection(null);
    setFinished(false);
    setQuestion(createRescueQuestion(level, 0));
    window.requestAnimationFrame(() => questionRef.current?.focus());
  };

  const choose = (answer) => {
    if (selection !== null || finished) return;
    setSelection(answer);
    if (answer !== question.answer) return;
    const nextScore = score + 10;
    setScore(nextScore);
    if (nextScore > best) {
      setBest(nextScore);
      try { localStorage.setItem(BEST_KEY, String(nextScore)); } catch { /* Storage is optional. */ }
    }
  };

  const next = () => {
    if (questionIndex + 1 === QUESTION_COUNT) {
      setFinished(true);
    } else {
      const nextIndex = questionIndex + 1;
      setQuestionIndex(nextIndex);
      setQuestion(createRescueQuestion(difficulty, nextIndex));
      setSelection(null);
    }
    window.requestAnimationFrame(() => questionRef.current?.focus());
  };

  return (
    <main className="rescue-page">
      <div className="rescue-floats" aria-hidden="true">{['+', '7', '÷', 'π', '×', '42'].map((symbol, index) => <span key={index}>{symbol}</span>)}</div>
      <div className="rescue-layout">
        <section className="rescue-intro" aria-labelledby="lost-heading">
          <div className="rescue-brand">K.R.A INITIATIVE / MISSION 404</div>
          <div className={`rescue-robot ${correct ? 'rescue-robot-happy' : ''}`} role="img" aria-label={correct ? 'Friendly robot celebrating your answer' : 'Friendly yellow robot looking confused'}>
            <div className="rescue-antenna" />
            <div className="rescue-head"><span className="rescue-eyebrow" /><div className="rescue-eyes"><i /><i /></div><div className="rescue-mouth" /></div>
            <div className="rescue-body"><span>404</span><i /><i /><i /></div>
            <div className="rescue-feet"><i /><i /></div>
            <span className="rescue-question-mark" aria-hidden="true">?</span>
          </div>
          <h1 id="lost-heading">404 — Oops! This Page Went Missing!</h1>
          <p>Looks like our little robot took a wrong turn! Help solve some math problems while we find our way home.</p>
          <div className="rescue-navigation"><Link to="/">Back to Homepage <span aria-hidden="true">↗</span></Link><Link to="/courses">Explore Courses <span aria-hidden="true">→</span></Link></div>
        </section>

        <section className="rescue-game" aria-labelledby="rescue-game-heading">
          <div className="rescue-game-top"><span className="rescue-mission">ROBOT RECOVERY / MATH LAB</span><span className="rescue-status-dot" aria-hidden="true" /></div>
          <h2 id="rescue-game-heading">Math Rescue Challenge</h2>
          <fieldset className="rescue-difficulty"><legend>Difficulty</legend><div>{['Easy', 'Medium', 'Hard'].map((level) => <label key={level}><input type="radio" name="rescue-difficulty" value={level} checked={difficulty === level} onChange={() => restart(level)} /><span>{level}</span></label>)}</div></fieldset>
          <div className="rescue-stats"><div>Score<strong>{score}<small> / 60</small></strong></div><div>Personal best<strong>{best}</strong></div></div>
          <progress aria-label="Question progress" value={finished ? QUESTION_COUNT : questionIndex} max={QUESTION_COUNT} />
          <div className="rescue-question-area">
            <div className="rescue-question-label">{finished ? 'MISSION COMPLETE' : `QUESTION ${questionIndex + 1} OF ${QUESTION_COUNT}`}</div>
            <h3 ref={questionRef} tabIndex={-1} className="rescue-equation">{finished ? `${score} / 60` : `${question.first} ${question.operation} ${question.second} = ?`}</h3>
            {finished ? <p className="rescue-result">{score >= 40 ? 'Robot rescued! Your problem-solving powered the way home.' : 'Great effort, explorer! Every question is a chance to learn.'}</p> : <div className="rescue-answers">{question.options.map((answer) => <button key={answer} type="button" disabled={selection !== null} onClick={() => choose(answer)} className={selection !== null && answer === question.answer ? 'rescue-answer-correct' : selection === answer ? 'rescue-answer-wrong' : ''}>{answer}{selection !== null && answer === question.answer ? <span aria-label="Correct answer"> ✓</span> : null}</button>)}</div>}
          </div>
          <div className="rescue-feedback" role="status" aria-live="polite">{selection !== null && !finished ? correct ? 'Power restored! +10 points. Nice thinking!' : `Keep exploring! The answer is ${question.answer}. You've got the next one.` : finished ? 'Ready for another rescue?' : 'Mission ready. Let’s find the missing answer.'}</div>
          {finished ? <button className="rescue-next" type="button" onClick={() => restart()}>Play Again <span aria-hidden="true">↻</span></button> : <button className="rescue-next" type="button" disabled={selection === null} onClick={next}>{questionIndex + 1 === QUESTION_COUNT ? 'Finish Mission' : 'Next Question'} <span aria-hidden="true">→</span></button>}
        </section>
      </div>
    </main>
  );
}